'use client';

import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions, ChartData, TooltipItem } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import styles from '../ShareholdingCharts.module.css';
import { LABELMAP } from '../label-constants';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

// Define the data point structure
interface ShareholdingDataPoint {
    date: Date;
    [key: string]: number | Date | string; // Dynamic fields for shareholding percentages
}

// Define component props
interface PieChartProps {
    data: ShareholdingDataPoint[];
    series: string[];
    title?: string;
    description?: string;
}

export function PieChart({ data, series, title, description }: PieChartProps): React.ReactElement {
    // Get the most recent data point
    const latestData: ShareholdingDataPoint = data.length > 0 ? data[data.length - 1] : {} as ShareholdingDataPoint;

    // Chart colors with transparency
    const backgroundColors: string[] = [
        'rgba(59, 130, 246, 0.8)',   // Blue
        'rgba(16, 185, 129, 0.8)',   // Green
        'rgba(249, 115, 22, 0.8)',   // Orange
        'rgba(139, 92, 246, 0.8)',   // Purple
        'rgba(239, 68, 68, 0.8)',    // Red
    ];

    const borderColors: string[] = [
        'rgba(59, 131, 246, 0)',   // Blue
        'rgba(16, 185, 129, 0)',   // Green
        'rgba(249, 116, 22, 0)',   // Orange
        'rgba(138, 92, 246, 0)',   // Purple
        'rgba(239, 68, 68, 0)',    // Red
    ];

    // Get label from LABELMAP or format the key
    const getLabel = (key: string): string => {
        return LABELMAP[key] || key
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const chartData: ChartData<'pie'> = {
        labels: series.map(getLabel),
        datasets: [
            {
                data: series.map(key => Number(latestData[key]) || 0),
                backgroundColor: backgroundColors,
                borderColor: borderColors,
                borderWidth: 1,
            },
        ],
    };

    const options: ChartOptions<'pie'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                    color: 'rgb(156, 163, 175)',
                    usePointStyle: true,
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 15,
                    font: {
                        size: 12,
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: TooltipItem<'pie'>) => {
                        const value = Number(context.raw);
                        // const total = context.dataset.data.reduce((a: number, b: number) => Number(a) + Number(b), 0);
                        // const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
                        // return `${context.label}: ${value.toFixed(2)}% (${percentage})`;
                        return `${value.toFixed(2)}%`;
                    }
                }
            }
        },
        animation: {
            animateRotate: true,
            animateScale: true
        },
        cutout: '65%',
    };

    return (
        <div className={styles.chartWrapper}>
            <Pie data={chartData} options={options} />
        </div>
    );
}